import React, { useEffect, useMemo, useState } from 'react'

import { Button, Popover, Textfield } from '@clientbase/clientbase-library'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import TuneIcon from '@mui/icons-material/Tune'
import { Checkbox } from '@mui/material'

import {
  DEFAULT_TABLE_DISPLAY,
  DENSITY_OPTIONS,
  FONT_SIZE_OPTIONS,
  type TableDensity,
  type TableDisplayPref,
  type TableFontSize
} from 'components/Table/tableDisplay'

import {
  Badge,
  ColumnsHead,
  ColumnsLabel,
  Container,
  Counter,
  Duo,
  DuoCol,
  Footer,
  FooterActions,
  Grip,
  Header,
  Item,
  Label,
  List,
  MiniLabel,
  ResetLink,
  ResetRow,
  SearchWrapper,
  SegmentButton,
  SegmentedGroup,
  Subtitle,
  Title
} from './ColumnSelectorPopover.styles'
import type { ColumnDef, TableColumnPref } from './types'

interface Props<TRow> {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  allColumns: ColumnDef<TRow>[]
  pref: TableColumnPref
  onApply: (next: TableColumnPref) => void
  onReset: () => void
}

interface SortableRowProps {
  id: string
  label: string
  checked: boolean
  onToggle: (checked: boolean) => void
}

const SortableRow: React.FC<SortableRowProps> = ({
  id,
  label,
  checked,
  onToggle
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition
  }
  // The whole row is the drag handle (except the checkbox). The grip stays as a
  // visual affordance only — listeners/attributes live on the <Item>.
  return (
    <Item
      ref={setNodeRef}
      style={style}
      $dragging={isDragging}
      {...attributes}
      {...listeners}
    >
      <Grip aria-hidden>
        <DragIndicatorIcon fontSize="inherit" />
      </Grip>
      <Checkbox
        size="small"
        checked={checked}
        onChange={(_, c) => onToggle(c)}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': label }}
        sx={{ p: '2px' }}
      />
      <Label>{label}</Label>
    </Item>
  )
}

function ColumnSelectorPopover<TRow>(props: Props<TRow>) {
  const { anchorEl, open, onClose, allColumns, pref, onApply, onReset } = props

  const [query, setQuery] = useState('')
  const [draftVisibility, setDraftVisibility] = useState<
    Record<string, boolean>
  >(pref.visibility)
  const [draftOrder, setDraftOrder] = useState<string[]>(pref.order)
  const [draftDisplay, setDraftDisplay] = useState<TableDisplayPref>({
    ...DEFAULT_TABLE_DISPLAY,
    ...pref.display
  })

  // Reset draft when popover opens or pref changes externally (e.g. after reset)
  useEffect(() => {
    if (open) {
      setDraftVisibility(pref.visibility)
      setDraftOrder(pref.order)
      setDraftDisplay({ ...DEFAULT_TABLE_DISPLAY, ...pref.display })
      setQuery('')
    }
  }, [open, pref])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  )

  const lockedCols = useMemo(
    () => allColumns.filter((c) => c.locked),
    [allColumns]
  )
  const unlockedCols = useMemo(
    () => allColumns.filter((c) => !c.locked),
    [allColumns]
  )

  // Apply draft order to unlocked columns for display
  const orderedUnlocked = useMemo(() => {
    if (!draftOrder.length) return unlockedCols
    const byId = new Map(unlockedCols.map((c) => [c.id, c]))
    const used = new Set<string>()
    const result: ColumnDef<TRow>[] = []
    draftOrder.forEach((id) => {
      const def = byId.get(id)
      if (def && !used.has(id)) {
        result.push(def)
        used.add(id)
      }
    })
    unlockedCols.forEach((c) => {
      if (!used.has(c.id)) result.push(c)
    })
    return result
  }, [unlockedCols, draftOrder])

  const filteredLocked = useMemo(() => {
    if (!query) return lockedCols
    const q = query.toLowerCase()
    return lockedCols.filter((c) => c.label.toLowerCase().includes(q))
  }, [lockedCols, query])

  const filteredUnlocked = useMemo(() => {
    if (!query) return orderedUnlocked
    const q = query.toLowerCase()
    return orderedUnlocked.filter((c) => c.label.toLowerCase().includes(q))
  }, [orderedUnlocked, query])

  const showSearch = allColumns.length > 6

  const isVisible = (col: ColumnDef<TRow>) => {
    if (col.locked) return true
    const v = draftVisibility[col.id]
    return v === undefined ? col.defaultVisible : v
  }

  const totalCount = allColumns.length
  const visibleCount = allColumns.filter(isVisible).length

  const handleToggle = (id: string, visible: boolean) => {
    setDraftVisibility((prev) => ({ ...prev, [id]: visible }))
  }

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const ids = orderedUnlocked.map((c) => c.id)
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex === -1 || newIndex === -1) return
    setDraftOrder(arrayMove(ids, oldIndex, newIndex))
  }

  const handleSave = () => {
    onApply({
      visibility: draftVisibility,
      order: draftOrder,
      display: draftDisplay
    })
    onClose()
  }

  const handleClose = () => {
    // discard draft
    setDraftVisibility(pref.visibility)
    setDraftOrder(pref.order)
    setDraftDisplay({ ...DEFAULT_TABLE_DISPLAY, ...pref.display })
    onClose()
  }

  const handleReset = () => {
    onClose()
    onReset()
  }

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      keepMounted={false}
      PaperProps={{
        sx: { padding: 0, borderRadius: '14px', overflow: 'hidden' }
      }}
    >
      <Container>
        <Header>
          <Title>
            <TuneIcon sx={{ fontSize: 18, color: '#3D892D' }} />
            Configurações da tabela
          </Title>
          <Subtitle>Ajuste a aparência, as colunas e a ordem.</Subtitle>
        </Header>

        <Duo>
          <DuoCol>
            <MiniLabel>Tamanho da fonte</MiniLabel>
            <SegmentedGroup role="group" aria-label="Tamanho da fonte">
              {FONT_SIZE_OPTIONS.map((opt) => (
                <SegmentButton
                  key={opt.value}
                  type="button"
                  $active={draftDisplay.fontSize === opt.value}
                  aria-pressed={draftDisplay.fontSize === opt.value}
                  onClick={() =>
                    setDraftDisplay((prev) => ({
                      ...prev,
                      fontSize: opt.value as TableFontSize
                    }))
                  }
                >
                  {opt.label}
                </SegmentButton>
              ))}
            </SegmentedGroup>
          </DuoCol>

          <DuoCol>
            <MiniLabel>Densidade</MiniLabel>
            <SegmentedGroup role="group" aria-label="Densidade">
              {DENSITY_OPTIONS.map((opt) => (
                <SegmentButton
                  key={opt.value}
                  type="button"
                  $active={draftDisplay.density === opt.value}
                  aria-pressed={draftDisplay.density === opt.value}
                  onClick={() =>
                    setDraftDisplay((prev) => ({
                      ...prev,
                      density: opt.value as TableDensity
                    }))
                  }
                >
                  {opt.label}
                </SegmentButton>
              ))}
            </SegmentedGroup>
          </DuoCol>
        </Duo>

        <ColumnsHead>
          <ColumnsLabel>Colunas</ColumnsLabel>
          <Counter>
            {visibleCount} de {totalCount} visíveis
          </Counter>
        </ColumnsHead>

        {showSearch && (
          <SearchWrapper>
            <Textfield
              size="small"
              placeholder="Buscar coluna…"
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(e.target.value)
              }
            />
          </SearchWrapper>
        )}

        <List>
          {filteredLocked.map((c) => (
            <Item key={c.id} $locked>
              <Grip $hidden>
                <DragIndicatorIcon fontSize="inherit" />
              </Grip>
              <Checkbox
                size="small"
                checked
                disabled
                inputProps={{ 'aria-label': c.label }}
                sx={{ p: '2px' }}
              />
              <Label>{c.label}</Label>
              <Badge>Fixa</Badge>
            </Item>
          ))}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedUnlocked.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {filteredUnlocked.map((c) => (
                <SortableRow
                  key={c.id}
                  id={c.id}
                  label={c.label}
                  checked={isVisible(c)}
                  onToggle={(v) => handleToggle(c.id, v)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </List>

        <Footer>
          <ResetRow>
            <ResetLink type="button" onClick={handleReset}>
              Restaurar padrão
            </ResetLink>
          </ResetRow>
          <FooterActions>
            <Button variantButton="neutral" onClick={handleClose}>
              Fechar
            </Button>
            <Button variantButton="action" onClick={handleSave}>
              Salvar
            </Button>
          </FooterActions>
        </Footer>
      </Container>
    </Popover>
  )
}

export default ColumnSelectorPopover
